import { db } from './db';
import type {
  AttachmentRow,
  QueueAttachmentRef,
  QueueEntry,
  QueueResource,
  QueueStatus,
} from './types';

interface EnqueueInput {
  clientId: string;
  resource: QueueResource;
  payload: unknown;
  attachments?: QueueAttachmentRef[];
  requiresSignature?: boolean;
}

function now(): string {
  return new Date().toISOString();
}

export const offlineQueue = {
  /** Insert a new pending entry. Returns the inserted row id. */
  async enqueue(input: EnqueueInput): Promise<number> {
    const entry: QueueEntry = {
      clientId: input.clientId,
      resource: input.resource,
      payload: input.payload,
      status: 'pending',
      retries: 0,
      createdAt: now(),
      updatedAt: now(),
      ...(input.attachments ? { attachments: input.attachments } : {}),
      ...(input.requiresSignature ? { requiresSignature: input.requiresSignature } : {}),
    };
    return db.queue.add(entry);
  },

  async saveAttachment(input: Omit<AttachmentRow, 'id' | 'createdAt'>): Promise<number> {
    return db.attachments.add({ ...input, createdAt: now() });
  },

  async getAttachment(id: number): Promise<AttachmentRow | undefined> {
    return db.attachments.get(id);
  },

  async listPending(): Promise<QueueEntry[]> {
    // pending OR scheduled for retry, ordered oldest first
    return db.queue
      .where('status')
      .anyOf(['pending', 'in-flight'] satisfies QueueStatus[])
      .sortBy('createdAt');
  },

  async listFailures(): Promise<QueueEntry[]> {
    return db.queue
      .where('status')
      .anyOf(['failed', 'requires-attention'] satisfies QueueStatus[])
      .sortBy('updatedAt');
  },

  async countPending(): Promise<number> {
    return db.queue.where('status').equals('pending').count();
  },

  async countFailures(): Promise<number> {
    return db.queue
      .where('status')
      .anyOf(['failed', 'requires-attention'] satisfies QueueStatus[])
      .count();
  },

  async markInFlight(id: number): Promise<void> {
    await db.queue.update(id, { status: 'in-flight', updatedAt: now() });
  },

  async markSynced(id: number): Promise<void> {
    const entry = await db.queue.get(id);
    await db.queue.update(id, { status: 'synced', updatedAt: now() });
    // Clean up associated attachment blobs once synced — they live on the server now.
    if (entry?.attachments?.length) {
      await db.attachments.bulkDelete(entry.attachments.map((a) => a.attachmentId));
    }
  },

  async markFailed(
    id: number,
    error: string,
    options: { permanent?: boolean; nextAttemptAt?: number } = {},
  ): Promise<void> {
    const entry = await db.queue.get(id);
    const retries = (entry?.retries ?? 0) + 1;
    await db.queue.update(id, {
      status: options.permanent ? 'requires-attention' : 'pending',
      retries,
      lastError: error,
      updatedAt: now(),
      ...(options.nextAttemptAt ? { nextAttemptAt: options.nextAttemptAt } : {}),
    });
  },

  /** Move a "requires-attention" record back into the pending queue for retry. */
  async retry(id: number): Promise<void> {
    // exactOptionalPropertyTypes blocks passing `undefined` explicitly; we
    // delete the optional fields via Dexie's modify() instead.
    await db.queue.where(':id').equals(id).modify((entry) => {
      entry.status = 'pending';
      entry.retries = 0;
      delete entry.lastError;
      delete entry.nextAttemptAt;
      entry.updatedAt = now();
    });
  },

  /** Permanently drop a record (and its attachments) — user-initiated. */
  async discard(id: number): Promise<void> {
    const entry = await db.queue.get(id);
    if (entry?.attachments?.length) {
      await db.attachments.bulkDelete(entry.attachments.map((a) => a.attachmentId));
    }
    await db.queue.delete(id);
  },

  /** Remove all entries marked synced — housekeeping after a successful flush. */
  async purgeSynced(): Promise<number> {
    return db.queue.where('status').equals('synced').delete();
  },

  /** Total bytes used by attachments — used by storage-pressure UI. */
  async attachmentBytes(): Promise<number> {
    let total = 0;
    await db.attachments.each((row) => {
      total += row.sizeBytes;
    });
    return total;
  },
};
